<template>
  <div>
    <header-component />
    <div class="px-2 py-2 w-full">
      <div class="card">
        <div class="flex justify-between items-center p-2">
          <h3
            class="mb-0 w-full font-bold text-gray-800 text-md"
            style="text-transform: capitalize"
          >
            Sales List
          </h3>
        </div>

        <!-- Filter Panel -->
        <div class="flex-shrink-0 px-4 py-2 mb-2 bg-gray-50 rounded-lg border border-gray-200">
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            <!-- Date Range -->
            <div class="col-span-2 space-y-2">
              <label class="block text-xs font-medium text-gray-600">Date Range</label>
              <div class="flex space-x-2">
                <input
                  v-model="panelFilters.fromDate"
                  type="date"
                  class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  v-model="panelFilters.toDate"
                  type="date"
                  class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div class="flex flex-wrap gap-1">
                <button type="button" @click="setDateFilter('currentFY')" :class="dateQuickFilterClass('currentFY')">Current FY</button>
                <button type="button" @click="setDateFilter('prevFY')" :class="dateQuickFilterClass('prevFY')">Prev FY</button>
                <button type="button" @click="setDateFilter('today')" :class="dateQuickFilterClass('today')">Today</button>
                <button type="button" @click="setDateFilter('yesterday')" :class="dateQuickFilterClass('yesterday')">Yesterday</button>
                <button type="button" @click="setDateFilter('thisWeek')" :class="dateQuickFilterClass('thisWeek')">This Week</button>
                <button type="button" @click="setDateFilter('thisMonth')" :class="dateQuickFilterClass('thisMonth')">This Month</button>
                <button type="button" @click="setDateFilter('lastMonth')" :class="dateQuickFilterClass('lastMonth')">Last Month</button>
              </div>
            </div>
            <!-- Branch -->
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-600">Branch</label>
              <select
                v-model="panelFilters.branch"
                class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Branches</option>
                <option v-for="branch in branches" :key="branch.id" :value="branch.id">{{ branch.name }}</option>
              </select>
            </div>
            <!-- Customer Name -->
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-600">Customer Name</label>
              <input
                v-model="panelFilters.customerName"
                type="text"
                @keyup.enter="applySingleFilter('customerName')"
                class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <!-- Customer Phone -->
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-600">Customer Phone</label>
              <input
                v-model="panelFilters.customerPhone"
                type="text"
                @keyup.enter="applySingleFilter('customerPhone')"
                class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <!-- Invoice Number -->
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-600">Invoice Number</label>
              <input
                v-model="panelFilters.invoiceNumber"
                type="text"
                @keyup.enter="applySingleFilter('invoiceNumber')"
                class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <!-- SR Number -->
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-600">SR Number</label>
              <input
                v-model="panelFilters.serviceRequestNumber"
                type="text"
                @keyup.enter="applySingleFilter('serviceRequestNumber')"
                class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <!-- Payment Status -->
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-600">Payment Status</label>
              <select
                v-model="panelFilters.paymentStatus"
                class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <!-- Actions -->
            <div class="space-y-2">
              <label class="block text-xs font-medium text-gray-600">Actions</label>
              <div class="flex space-x-2">
                <button
                  @click="applyFilters"
                  class="flex-1 px-3 py-2 text-sm text-white bg-blue-600 rounded-md transition-colors hover:bg-blue-700"
                >Apply</button>
                <button
                  @click="clearFilters"
                  class="flex-1 px-3 py-2 text-sm text-white bg-gray-500 rounded-md transition-colors hover:bg-gray-600"
                >Clear</button>
              </div>
            </div>
          </div>

          <!-- Active Filters -->
          <div v-if="hasActiveFilters" class="pt-4 mt-4 border-t border-gray-200">
            <div class="flex flex-wrap gap-2 items-center">
              <span class="text-xs font-medium text-gray-600">Active Filters:</span>
              <span
                v-if="appliedFilters.fromDate || appliedFilters.toDate"
                class="inline-flex items-center px-2 py-1 text-xs text-blue-800 bg-blue-100 rounded-full"
              >
                Date: {{ formatAppliedDateRange() }}
                <button @click="clearDateFilter" class="ml-1 text-blue-600 hover:text-blue-800">×</button>
              </span>
              <span
                v-if="appliedFilters.branch"
                class="inline-flex items-center px-2 py-1 text-xs text-purple-800 bg-purple-100 rounded-full"
              >
                Branch: {{ getBranchName(appliedFilters.branch) }}
                <button @click="clearAppliedFilter('branch')" class="ml-1 text-purple-600 hover:text-purple-800">×</button>
              </span>
              <span
                v-if="appliedFilters.customerName"
                class="inline-flex items-center px-2 py-1 text-xs text-green-800 bg-green-100 rounded-full"
              >
                Customer: {{ appliedFilters.customerName }}
                <button @click="clearAppliedFilter('customerName')" class="ml-1 text-green-600 hover:text-green-800">×</button>
              </span>
              <span
                v-if="appliedFilters.customerPhone"
                class="inline-flex items-center px-2 py-1 text-xs text-green-800 bg-green-100 rounded-full"
              >
                Phone: {{ appliedFilters.customerPhone }}
                <button @click="clearAppliedFilter('customerPhone')" class="ml-1 text-green-600 hover:text-green-800">×</button>
              </span>
              <span
                v-if="appliedFilters.invoiceNumber"
                class="inline-flex items-center px-2 py-1 text-xs text-orange-800 bg-orange-100 rounded-full"
              >
                Invoice: {{ appliedFilters.invoiceNumber }}
                <button @click="clearAppliedFilter('invoiceNumber')" class="ml-1 text-orange-600 hover:text-orange-800">×</button>
              </span>
              <span
                v-if="appliedFilters.serviceRequestNumber"
                class="inline-flex items-center px-2 py-1 text-xs text-teal-800 bg-teal-100 rounded-full"
              >
                SR No: {{ appliedFilters.serviceRequestNumber }}
                <button @click="clearAppliedFilter('serviceRequestNumber')" class="ml-1 text-teal-600 hover:text-teal-800">×</button>
              </span>
              <span
                v-if="appliedFilters.paymentStatus"
                class="inline-flex items-center px-2 py-1 text-xs text-red-800 bg-red-100 rounded-full"
              >
                Status: {{ appliedFilters.paymentStatus }}
                <button @click="clearAppliedFilter('paymentStatus')" class="ml-1 text-red-600 hover:text-red-800">×</button>
              </span>
            </div>
          </div>
        </div>

        <!-- Custom Tailwind CSS Table -->
        <div class="overflow-x-auto">
          <table
            class="min-w-full bg-white border border-gray-200"
            style="table-layout: fixed;"
          >
            <thead class="bg-gray-50">
              <tr>
                <th
                  class="px-2 py-2 w-20 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b"
                >
                  Branch
                </th>
                <th
                  class="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b"
                  style="width: 100px;"
                >
                  Customer
                </th>
                <th
                  class="px-2 py-2 w-20 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b"
                >
                  Date
                </th>
                <th
                  class="px-2 py-2 w-24 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b"
                >
                  Invoice No.
                </th>
                <th
                  class="px-2 py-2 w-16 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b"
                >
                  SR. No.
                </th>
                <th
                  class="px-2 py-2 w-24 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b"
                >
                  Chalans
                </th>
                <th
                  class="px-2 py-2 w-20 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b"
                >
                  Total
                </th>
                <th
                  class="px-2 py-2 w-16 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b"
                >
                  Disc
                </th>
                <th
                  class="px-2 py-2 w-20 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b"
                >
                  Paid
                </th>
                <th
                  class="px-2 py-2 w-16 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b"
                >
                  Payment
                </th>
                <th
                  class="px-2 py-2 w-20 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b"
                >
                  Created By
                </th>
                <th
                  class="px-2 py-2 w-16 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <!-- Data Rows -->
              <tr
                v-for="sale in tableData"
                :key="sale.saleId"
                class="hover:bg-gray-50"
              >
                <td class="px-2 py-2 text-xs whitespace-nowrap border-b">
                  <span
                    v-if="sale.branchName"
                    class="inline-flex px-1 py-0.5 text-xs font-semibold rounded-full"
                    :style="branchBadgeStyle(sale.branchColor)"
                  >
                    {{ sale.branchName }}
                  </span>
                </td>
                <td class="px-2 py-2 text-xs text-gray-900 border-b">
                  <div
                    class="w-full truncate"
                    :title="sale.user.firstName + ' ' + sale.user.lastName"
                  >
                    {{ sale.user.firstName + " " + sale.user.lastName }}
                  </div>
                </td>
                <td
                  class="px-2 py-2 text-xs text-gray-900 whitespace-nowrap border-b"
                >
                  {{ formatDate(sale.date) }}
                </td>
                <td
                  class="px-2 py-2 text-xs text-gray-900 whitespace-nowrap border-b"
                >
                  {{ sale.invoiceNumber }}
                </td>
                <td
                  class="px-2 py-2 text-xs text-gray-900 whitespace-nowrap border-b"
                >
                  {{ sale.serviceRequestNumber || "-" }}
                </td>
                <td class="px-2 py-2 text-xs border-b">
                  <div class="flex flex-col gap-0.5">
                    <template v-if="sale.srChalanNumbers">
                      <router-link
                        v-for="num in sale.srChalanNumbers.split(', ')"
                        :key="'sr-' + num"
                        :to="{ path: '/chalan/list', query: { chalanNumber: num.trim() } }"
                        class="text-blue-600 hover:underline"
                      >{{ num.trim() }}</router-link>
                    </template>
                    <template v-if="sale.customerChalanNumbers">
                      <router-link
                        v-for="num in sale.customerChalanNumbers.split(', ')"
                        :key="'cc-' + num"
                        :to="{ path: '/customer-chalans', query: { chalanNumber: num.trim() } }"
                        class="text-indigo-600 hover:underline"
                      >{{ num.trim() }}</router-link>
                    </template>
                    <span v-if="!sale.srChalanNumbers && !sale.customerChalanNumbers" class="text-gray-400">—</span>
                  </div>
                </td>
                <td
                  class="px-2 py-2 text-xs text-gray-900 whitespace-nowrap border-b"
                >
                  {{ formatCurrency(sale.total) }}
                </td>
                <td
                  class="px-2 py-2 text-xs text-gray-900 whitespace-nowrap border-b"
                >
                  <span v-if="sale.isPercentage === true">
                    {{ sale.discount }}%
                  </span>
                  <span v-else> ₹{{ sale.discount }} </span>
                </td>
                <td
                  class="px-2 py-2 text-xs text-gray-900 whitespace-nowrap border-b"
                >
                  {{ formatCurrency(sale.amountPaid) }}
                </td>
                <td class="px-2 py-2 text-xs whitespace-nowrap border-b">
                  <div class="flex flex-col gap-0.5">
                    <span
                      v-if="sale.total > sale.amountPaid"
                      class="inline-flex px-1 py-0.5 text-xs font-semibold text-yellow-800 bg-yellow-100 rounded-full"
                    >
                      Pending
                    </span>
                    <span
                      v-else
                      class="inline-flex px-2 py-0.5 text-xs font-semibold text-green-800 bg-green-100 rounded-full"
                    >
                      Paid
                    </span>
                    <span
                      v-if="returnedSaleIds[sale.saleId]"
                      class="inline-flex px-1 py-0.5 text-xs font-semibold text-red-800 bg-red-100 rounded-full"
                    >
                      Returned
                    </span>
                  </div>
                </td>
                <td class="px-2 py-2 text-xs text-gray-900 whitespace-nowrap border-b">
                  {{ sale.creatorFirstName }} {{ sale.creatorLastName }}
                </td>
                <td
                  class="px-2 py-2 text-xs text-gray-900 whitespace-nowrap border-b"
                >
                  <div class="inline-block relative text-left">
                    <button
                      @click="toggleDropdown(sale.saleId)"
                      class="inline-flex justify-center items-center w-8 h-8 text-gray-600 rounded-full hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <svg
                        class="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"
                        />
                      </svg>
                    </button>

                    <!-- Dropdown Menu -->
                    <div
                      v-if="activeDropdown === sale.saleId"
                      class="absolute right-0 z-10 mt-1 w-40 bg-white rounded-md border border-gray-200 ring-1 ring-black ring-opacity-5 shadow-lg origin-top-right"
                    >
                      <div class="py-1">
                        <button
                          @click="handleAction(sale, 'creditNotes')"
                          class="block px-3 py-2 w-full text-xs text-left text-gray-700 hover:bg-gray-100"
                        >
                          Add Credit Notes
                        </button>
                        <button
                          @click="handleAction(sale, 'saleTransactions')"
                          class="block px-3 py-2 w-full text-xs text-left text-gray-700 hover:bg-gray-100"
                        >
                          Show Sale Transactions
                        </button>
                        <button
                          @click="handleAction(sale, 'editSale')"
                          class="block px-3 py-2 w-full text-xs text-left text-gray-700 hover:bg-gray-100"
                        >
                          Edit Sale
                        </button>
                        <button
                          @click="handleAction(sale, 'showDetails')"
                          class="block px-3 py-2 w-full text-xs text-left text-gray-700 hover:bg-gray-100"
                        >
                          Print Receipt
                        </button>
                        <button
                          @click="handleAction(sale, 'whatsappPrint')"
                          class="block px-3 py-2 w-full text-xs text-left text-gray-700 hover:bg-gray-100"
                        >
                          WhatsApp Bill Print
                        </button>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>

              <!-- Summary Row -->
              <tr v-if="meta" class="font-semibold bg-gray-50">
                <td
                  class="px-2 py-2 text-xs text-gray-900 whitespace-nowrap border-b"
                >
                  Total
                </td>
                <td
                  class="px-2 py-2 text-xs text-gray-900 whitespace-nowrap border-b"
                >
                  N/A
                </td>
                <td
                  class="px-2 py-2 text-xs text-gray-900 whitespace-nowrap border-b"
                >
                  N/A
                </td>
                <td
                  class="px-2 py-2 text-xs text-gray-900 whitespace-nowrap border-b"
                >
                  N/A
                </td>
                <td
                  class="px-2 py-2 text-xs text-gray-900 whitespace-nowrap border-b"
                >
                  N/A
                </td>
                <td
                  class="px-2 py-2 text-xs text-gray-900 whitespace-nowrap border-b"
                >
                  {{ formatCurrency(meta.grandTotal) }}
                </td>
                <td
                  class="px-2 py-2 text-xs text-gray-900 whitespace-nowrap border-b"
                >
                  N/A
                </td>
                <td
                  class="px-2 py-2 text-xs text-gray-900 whitespace-nowrap border-b"
                >
                  {{ formatCurrency(meta.paidTotal) }}
                </td>
                <td
                  class="px-2 py-2 text-xs text-gray-900 whitespace-nowrap border-b"
                >
                  N/A
                </td>
                <td
                  class="px-2 py-2 text-xs text-gray-900 whitespace-nowrap border-b"
                ></td>
                <td
                  class="px-2 py-2 text-xs text-gray-900 whitespace-nowrap border-b"
                >
                  N/A
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="card-footer">
          <div
            v-if="meta"
            slot="footer"
            class="flex-wrap col-12 d-flex justify-content-center justify-content-sm-between"
          >
            <div class>
              <p class="card-category">
                Showing {{ meta.from + 1 }} to {{ meta.to }} of
                {{ meta.total }} entries
                <span v-if="selectedRows.length"
                  >&nbsp; &nbsp; {{ selectedRows.length }} rows selected</span
                >
              </p>
            </div>
            <pagination :meta="meta" @paginate="setPagination" />
          </div>
        </div>
      </div>
      <el-dialog :visible.sync="showBill" width="60%" title="Bill">
        <show-bill />
      </el-dialog>
      <el-dialog :visible.sync="showTransaction" title="Show Transaction">
        <show-transaction @close="showTransaction = false" />
      </el-dialog>
      <el-dialog
        @open="onDialogOpen"
        @close="onDialogClose"
        :visible.sync="showWhatsappPrintModal"
        width="60%"
        title="Bill"
      >
        <whatsapp-bill-print ref="saleInvoice" />
      </el-dialog>
    </div>
  </div>
</template>

<script>
import {
  Select,
  Option,
  DatePicker,
  DropdownMenu,
  DropdownItem,
  Dropdown,
  Dialog
} from "element-ui";
import { mapGetters, mapActions, mapMutations } from "vuex";
import { format } from "date-fns";
import showBill from "./ShowBill";
import ShowTransaction from "./ShowTransaction";
import headerComponent from "../Components/HeaderComponent";
import pagination from "@/components/AmsaPagination";
import WhatsAppBillPrint from "./WhatsAppBillPrint.vue";
import { apiHelper } from "../../utils/helpers";
import endpoints from "../../utils/constants";

function formatLocalDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function calendarMonthRange(offset = 0, refDate = new Date()) {
  const y = refDate.getFullYear();
  const m = refDate.getMonth() + offset;
  return {
    fromDate: formatLocalDate(new Date(y, m, 1)),
    toDate: formatLocalDate(new Date(y, m + 1, 0))
  };
}

function indianFinancialYearRange(period = "current", refDate = new Date()) {
  const y = refDate.getFullYear();
  const month = refDate.getMonth();
  let fyStartYear = month >= 3 ? y : y - 1;
  if (period === "prev") fyStartYear -= 1;
  return {
    fromDate: `${fyStartYear}-04-01`,
    toDate: `${fyStartYear + 1}-03-31`
  };
}

export default {
  filters: {
    convertDate(date) {
      return format(date, "YYYY-MM-DD");
    },
    currency(value) {
      const amount = parseFloat(value) || 0;
      const a = new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR"
      }).format(amount);
      return a.replace(".00", "");
    }
  },
  components: {
    pagination,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker,
    [Dialog.name]: Dialog,
    "show-bill": showBill,
    "show-transaction": ShowTransaction,
    "header-component": headerComponent,
    "whatsapp-bill-print": WhatsAppBillPrint
  },
  data() {
    const defaultFY = indianFinancialYearRange("current");
    return {
      showBill: false,
      showTransaction: false,
      selectedRows: [],
      pageSize: 10,
      branches: [],
      showWhatsappPrintModal: false,
      activeDropdown: null,
      returnedSaleIds: {},
      panelFilters: {
        fromDate: defaultFY.fromDate,
        toDate: defaultFY.toDate,
        branch: "",
        customerName: "",
        customerPhone: "",
        invoiceNumber: "",
        serviceRequestNumber: "",
        paymentStatus: ""
      },
      appliedFilters: {
        fromDate: defaultFY.fromDate,
        toDate: defaultFY.toDate,
        branch: "",
        customerName: "",
        customerPhone: "",
        invoiceNumber: "",
        serviceRequestNumber: "",
        paymentStatus: ""
      },
      _skipFilterWatchFetch: false,
      localSaleList: { rows: [], meta: null }
    };
  },
  computed: {
    tableData() {
      return this.localSaleList.rows;
    },
    meta() {
      return this.localSaleList.meta;
    },
    hasActiveFilters() {
      return Object.values(this.appliedFilters).some(v => v !== "");
    },
    activeDatePreset() {
      const f = this.panelFilters.fromDate;
      const t = this.panelFilters.toDate;
      if (!f || !t) return null;

      const cur = indianFinancialYearRange("current");
      if (f === cur.fromDate && t === cur.toDate) return "currentFY";

      const prev = indianFinancialYearRange("prev");
      if (f === prev.fromDate && t === prev.toDate) return "prevFY";

      const today = new Date();
      const todayStr = formatLocalDate(today);
      if (f === todayStr && t === todayStr) return "today";

      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = formatLocalDate(yesterday);
      if (f === yesterdayStr && t === yesterdayStr) return "yesterday";

      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      if (f === formatLocalDate(startOfWeek) && t === todayStr) return "thisWeek";

      const thisMonth = calendarMonthRange(0, today);
      if (f === thisMonth.fromDate && t === thisMonth.toDate) return "thisMonth";

      const lastMonth = calendarMonthRange(-1, today);
      if (f === lastMonth.fromDate && t === lastMonth.toDate) return "lastMonth";

      return null;
    }
  },
  watch: {
    "panelFilters.fromDate"(newVal) {
      this.appliedFilters.fromDate = newVal;
      if (!this._skipFilterWatchFetch) this.fetchSales(1);
    },
    "panelFilters.toDate"(newVal) {
      this.appliedFilters.toDate = newVal;
      if (!this._skipFilterWatchFetch) this.fetchSales(1);
    },
    "panelFilters.branch"(newVal) {
      this.appliedFilters.branch = newVal;
      if (!this._skipFilterWatchFetch) this.fetchSales(1);
    },
    "panelFilters.paymentStatus"(newVal) {
      this.appliedFilters.paymentStatus = newVal;
      if (!this._skipFilterWatchFetch) this.fetchSales(1);
    }
  },
  mounted() {
    this.getBranches();
    this.fetchSales();
    this.fetchReturnedSaleIds();
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  methods: {
    fetchReturnedSaleIds() {
      apiHelper
        .call("get", endpoints.LARAVEL_BASE_URL + "/api/v1/credit-notes/returned-sale-ids")
        .then(res => {
          const map = {};
          (res.data.sale_ids || []).forEach(id => { map[id] = true; });
          this.returnedSaleIds = map;
        })
        .catch(() => {});
    },
    branchBadgeStyle(color) {
      if (!color) return { background: '#dbeafe', color: '#1e40af' };
      return { background: color, color: '#fff' };
    },
    ...mapActions({
      editSale: "sale/editSale",
      getReceipt: "sale/getReceipt",
      getReceiptDataForWhatsapp: "sale/getReceiptDataForWhatsapp",
      getSaleTransactions: "sale/getSaleTransactions"
    }),
    ...mapMutations({
      setSale: "sale/setSale",
      setSaleUser: "sale/setSaleUser"
    }),
    fetchSales(page = 1) {
      const params = [`page=${page}`, `perPage=${this.pageSize}`];
      if (this.appliedFilters.fromDate) params.push(`fromDate=${this.appliedFilters.fromDate}`);
      if (this.appliedFilters.toDate) params.push(`toDate=${this.appliedFilters.toDate}`);
      if (this.appliedFilters.branch) params.push(`branch=${encodeURIComponent(this.appliedFilters.branch)}`);
      if (this.appliedFilters.customerName) params.push(`customerName=${encodeURIComponent(this.appliedFilters.customerName)}`);
      if (this.appliedFilters.customerPhone) params.push(`customerPhone=${encodeURIComponent(this.appliedFilters.customerPhone)}`);
      if (this.appliedFilters.invoiceNumber) params.push(`invoiceNumber=${encodeURIComponent(this.appliedFilters.invoiceNumber)}`);
      if (this.appliedFilters.serviceRequestNumber) params.push(`serviceRequestNumber=${encodeURIComponent(this.appliedFilters.serviceRequestNumber)}`);
      if (this.appliedFilters.paymentStatus) params.push(`paymentStatus=${encodeURIComponent(this.appliedFilters.paymentStatus)}`);
      const url = `${endpoints.LARAVEL_BASE_URL}/api/v1/sales-list?${params.join("&")}`;
      apiHelper.call("get", url).then(res => {
        this.localSaleList = res.data;
      }).catch(() => {
        this.$notify({ message: "Failed to load sales.", type: "danger", title: "Error" });
      });
    },
    setPagination(data) {
      this.pageSize = data.perPage;
      this.fetchSales(data.page);
    },
    applyFilters() {
      this.appliedFilters = { ...this.panelFilters };
      this.fetchSales(1);
    },
    applySingleFilter(filterName) {
      this.appliedFilters[filterName] = this.panelFilters[filterName];
      this.fetchSales(1);
    },
    clearAppliedFilter(filterName) {
      this.appliedFilters[filterName] = "";
      this.panelFilters[filterName] = "";
      this.fetchSales(1);
    },
    clearDateFilter() {
      this._skipFilterWatchFetch = true;
      this.panelFilters.fromDate = "";
      this.panelFilters.toDate = "";
      this.$nextTick(() => {
        this._skipFilterWatchFetch = false;
        this.appliedFilters.fromDate = "";
        this.appliedFilters.toDate = "";
        this.fetchSales(1);
      });
    },
    clearFilters() {
      this._skipFilterWatchFetch = true;
      const empty = {
        fromDate: "", toDate: "", branch: "", customerName: "",
        customerPhone: "", invoiceNumber: "", serviceRequestNumber: "", paymentStatus: ""
      };
      this.panelFilters = { ...empty };
      this.appliedFilters = { ...empty };
      this.$nextTick(() => {
        this._skipFilterWatchFetch = false;
        this.fetchSales(1);
      });
    },
    setDateFilter(type) {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());

      let range;
      switch (type) {
        case "currentFY": range = indianFinancialYearRange("current"); break;
        case "prevFY": range = indianFinancialYearRange("prev"); break;
        case "today": range = { fromDate: formatLocalDate(today), toDate: formatLocalDate(today) }; break;
        case "yesterday": range = { fromDate: formatLocalDate(yesterday), toDate: formatLocalDate(yesterday) }; break;
        case "thisWeek": range = { fromDate: formatLocalDate(startOfWeek), toDate: formatLocalDate(today) }; break;
        case "thisMonth": range = calendarMonthRange(0, today); break;
        case "lastMonth": range = calendarMonthRange(-1, today); break;
        default: return;
      }
      this._skipFilterWatchFetch = true;
      this.panelFilters.fromDate = range.fromDate;
      this.panelFilters.toDate = range.toDate;
      this.$nextTick(() => {
        this._skipFilterWatchFetch = false;
        this.appliedFilters.fromDate = range.fromDate;
        this.appliedFilters.toDate = range.toDate;
        this.fetchSales(1);
      });
    },
    dateQuickFilterClass(preset) {
      const selected = this.activeDatePreset === preset;
      return [
        "px-2 py-1 text-xs rounded transition-colors",
        selected
          ? "text-blue-700 bg-blue-100 hover:bg-blue-200"
          : "text-gray-700 bg-gray-100 hover:bg-gray-200"
      ];
    },
    formatAppliedDateRange() {
      const f = this.appliedFilters.fromDate;
      const t = this.appliedFilters.toDate;
      if (f && t) {
        if (f === t) return new Date(f).toLocaleDateString("en-IN");
        return `${new Date(f).toLocaleDateString("en-IN")} - ${new Date(t).toLocaleDateString("en-IN")}`;
      }
      if (f) return `From ${new Date(f).toLocaleDateString("en-IN")}`;
      if (t) return `Until ${new Date(t).toLocaleDateString("en-IN")}`;
      return "";
    },
    getBranchName(branchId) {
      const branch = this.branches.find(b => b.id == branchId);
      return branch ? branch.name : branchId;
    },
    handleAction(sale, command) {
      if (command === "creditNotes") {
        sale.user.id = sale.userId;
        this.setSaleUser(sale.user);
        this.$router.push("/pos/" + sale.saleId + "/credit-notes/");
      } else if (command === "showDetails") {
        this.getReceipt(sale.saleId).then(status => {});
      } else if (command === "saleTransactions") {
        this.setSale(sale);
        this.getSaleTransactions(sale.saleId);
        this.showTransaction = true;
      } else if (command === "editSale") {
        const isFullyPaid = sale.amountPaid > 0 && sale.total > 0 && sale.amountPaid >= sale.total;
        if (isFullyPaid) {
          this.$notify({
            message: "This sale is fully paid and cannot be edited. Use \"Add Credit Notes\" to process a return.",
            type: "warning",
            title: "Cannot Edit"
          });
        } else {
          this.editSale(sale.saleId).then(status => {
            if (status === 200) {
              this.$router.push(`/pos/${sale.saleId}/`);
            }
          });
        }
      } else if (command === "whatsappPrint") {
        this.getReceiptDataForWhatsapp(sale.saleId).then(status => {
          if (status === 200) {
            this.showWhatsappPrintModal = true;
          }
        });
      }
    },
    getBranches() {
      apiHelper
        .call("get", `${endpoints.LARAVEL_BASE_URL}/api/v1/branches`)
        .then(res => {
          this.branches = res.data.data;
        })
        .catch(() => {
          this.$notify({
            message: "Something Went Wrong!",
            type: "danger",
            title: "error"
          });
        });
    },
    onDialogClose() {
      if (this.$refs && this.$refs.saleInvoice) {
        this.$refs.saleInvoice.resetProperty();
      }
    },
    onDialogOpen() {
      if (this.$refs && this.$refs.saleInvoice) {
        this.$refs.saleInvoice.fetchData();
      }
    },
    formatDate(date) {
      return format(date, "YYYY-MM-DD");
    },
    formatCurrency(value) {
      const amount = parseFloat(value) || 0;
      const a = new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR"
      }).format(amount);
      return a.replace(".00", "");
    },
    toggleDropdown(saleId) {
      this.activeDropdown = this.activeDropdown === saleId ? null : saleId;
    },
    handleClickOutside(event) {
      if (!event.target.closest(".relative")) {
        this.activeDropdown = null;
      }
    }
  }
};
</script>

<style lang="scss">
.el-dropdown-link {
  cursor: pointer;
  color: #409eff;
}
.el-icon-arrow-down {
  font-size: 12px;
}

.sales-table {
  .el-table__row {
    text-transform: capitalize;
  }
}
.no-border-card .card-footer {
  border-top: 0;
}
.baner-branch {
  background-color: #d2f5f1;
  border-color: #c4e4e1;
  color: #60c8be;
}
</style>
